import type { ReactNode } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";
import { CHART_RAMP_TOKENS, useChartColors, type ChartColorToken } from "./colors";
import {
  ACTIVE_DOT_TRAVEL_CLASS,
  AXIS_TICK,
  CHART_MOTION,
  HOVER_CURSOR_TRAVEL_CLASS,
  TOOLTIP_MOTION,
  TooltipShell,
  useChartMotion,
} from "./internal/recharts";

/** One value series drawn over the shared x axis. */
export type TimeSeriesChartSeries = Omittable<{
  /** Row field holding this series' value. */
  key: string;
  /** Semantic color; series without one take the ramp in declaration order. */
  colorToken?: ChartColorToken;
}>;

export type TimeSeriesChartProps<D extends object> = Omittable<{
  /** One row per x position, each series' value stored under its `key`. */
  data: readonly D[];
  /** Row field holding the x value (a bucket-start timestamp or similar). */
  xKey: string;
  series: readonly TimeSeriesChartSeries[];
  /** Fill under the value edge — the single-series trend treatment. */
  area?: boolean;
  /** Line interpolation. Defaults to `linear`. */
  curve?: "linear" | "monotone";
  showGrid?: boolean;
  showAxes?: boolean;
  /** Cap on x tick labels; ticks are sampled evenly from the first bucket. */
  maxXTicks?: number;
  xTickFormatter?: (value: number) => string;
  yTickFormatter?: (value: number) => string;
  /** Press on the plot — answers the hovered row (every series' bucket). */
  onPointClick?: (row: D) => void;
  /** Hover card content for the hovered row; omit for no hover card. */
  renderTooltip?: (row: D) => ReactNode;
  ariaLabel?: string;
}>;

/**
 * The finished time-series chart (recharts engine): one shared x axis, any
 * number of line series, optional area fill for a single-series trend. Colors
 * are semantic tokens painted as live `var()` strings; recharts never crosses
 * this surface.
 */
export function TimeSeriesChart<D extends object>({
  data,
  xKey,
  series,
  area,
  curve = "linear",
  showGrid,
  showAxes,
  maxXTicks,
  xTickFormatter,
  yTickFormatter,
  onPointClick,
  renderTooltip,
  ariaLabel,
}: TimeSeriesChartProps<D>) {
  const c = useChartColors();
  const animate = useChartMotion();

  // recharts' own interval sampling reacts to measured label widths; a widget
  // sizes ticks from its span instead, so the cap maps to a fixed stride.
  const interval =
    maxXTicks !== undefined && data.length > maxXTicks
      ? Math.max(0, Math.ceil(data.length / maxXTicks) - 1)
      : 0;

  const handleClick = onPointClick
    ? (state: { activeTooltipIndex?: number | string | null | undefined }) => {
        const raw = state.activeTooltipIndex;
        const index = typeof raw === "string" ? Number(raw) : raw;
        if (index === undefined || index === null || !Number.isInteger(index))
          return;
        const row = data[index];
        if (row !== undefined) onPointClick(row);
      }
    : undefined;

  return (
    <Box
      width="100%"
      height="100%"
      role={ariaLabel === undefined ? undefined : "img"}
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data as D[]}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          {...(handleClick ? { onClick: handleClick, cursor: "pointer" } : null)}
        >
          {showGrid ? <CartesianGrid vertical={false} stroke={c.grid} /> : null}
          <XAxis
            dataKey={xKey}
            hide={!showAxes}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            interval={interval}
            {...(xTickFormatter ? { tickFormatter: xTickFormatter } : null)}
          />
          <YAxis
            hide={!showAxes}
            width={36}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            tickCount={4}
            allowDecimals={false}
            {...(yTickFormatter ? { tickFormatter: yTickFormatter } : null)}
          />
          {renderTooltip ? (
            <Tooltip
              isAnimationActive={animate}
              {...TOOLTIP_MOTION}
              // The hovered bucket is marked in three places at once — this
              // card, the guide line, one dot per series — so all three travel
              // to the next bucket on the same timing.
              cursor={{ stroke: c.grid, className: HOVER_CURSOR_TRAVEL_CLASS }}
              content={({ active, payload }) => {
                const row = payload?.[0]?.payload as D | undefined;
                if (!active || row === undefined) return null;
                return <TooltipShell>{renderTooltip(row)}</TooltipShell>;
              }}
            />
          ) : null}
          {series.map((s, i) => {
            const paint = c.paint(
              s.colorToken ?? CHART_RAMP_TOKENS[i % CHART_RAMP_TOKENS.length],
            );
            return area && series.length === 1 ? (
              <Area
                key={s.key}
                dataKey={s.key}
                type={curve}
                stroke={paint}
                strokeWidth={2}
                fill={`color-mix(in oklab, ${paint} 18%, transparent)`}
                activeDot={{ r: 3.5, strokeWidth: 0, className: ACTIVE_DOT_TRAVEL_CLASS }}
                isAnimationActive={animate}
                {...CHART_MOTION}
              />
            ) : (
              <Line
                key={s.key}
                dataKey={s.key}
                type={curve}
                stroke={paint}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3.5, strokeWidth: 0, className: ACTIVE_DOT_TRAVEL_CLASS }}
                isAnimationActive={animate}
                {...CHART_MOTION}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}
